import { sourceInputAllowed } from '@/simulation/native/authority';
import { pixelControl, pixelRect } from '@/utils/tilePixels';
import { colors } from '@/styles';
import { Signal } from '@/models/data/Signal';
import { TileType } from '@/models/TileTypes';
import {
  defaultTileGraphics,
  connectionGraphics,
} from '@/models/tiles/DefaultTileGraphics';
import {
  BuildAndConnectNodesFn,
  CreateActionsFn,
  CreateDefaultStateFn,
  TileGraphicsDefinition,
  ContextMenuActionFn,
  CreateContextMenuItemsData,
  TileSideIO,
} from '@/models/tiles/TileDefinition';
import { InterfaceTileState, Side, TileSideIOType } from '@/schemas/tiles';
import {
  drawInterfaceTileGraphics,
  InterfaceTileDefinition,
  getInterfaceLinkFromTile,
  invalidateInterfaceLinkTiles,
} from './InterfaceTile';
import { Color } from 'pixi.js';
import { InterfaceLinkInstanceId } from '@/types/ids';
import { standardBuildAndConnect, wireBuildAndConnect } from '@/models/logic/utils';
import { InterfaceLink, SIInputMode } from '@/models/ChipModel';
import { LogicGraph } from '@/models/logic/LogicGraph';
import { TileInstance } from './TileInstance';
import { LogicNodeInstance } from '@/models/logic/LogicNodeInstance';
import { LeverNodeState } from '@/models/logic/nodes';
import { distinct } from '@/utils/object';

export type SingleInterfaceTileStateBase = {
  inputMode: SIInputMode;
  toggleState: Signal;
};
export type SingleInterfaceTileState = InterfaceTileState<SingleInterfaceTileStateBase>;

const allOutputSides: Record<Side, TileSideIO> = {
  [Side.TOP]: { type: TileSideIOType.OUTPUT, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
  [Side.BOTTOM]: { type: TileSideIOType.OUTPUT, forceConnection: false },
  [Side.LEFT]: { type: TileSideIOType.OUTPUT, forceConnection: false },
};

const allInputOutputSides: Record<Side, TileSideIO> = {
  [Side.TOP]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
  [Side.RIGHT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
  [Side.BOTTOM]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
  [Side.LEFT]: { type: TileSideIOType.INPUT_OUTPUT, forceConnection: false },
};

function getInterfaceLinkForInstance(
  instance: TileInstance<SingleInterfaceTileState>,
): InterfaceLink | undefined {
  const chipEditor = instance.context?.editor?.currentChipEditor;
  if (!chipEditor) return undefined;
  const linkId = instance.state.connectedInterface
    ?.interfaceLinkId as InterfaceLinkInstanceId;
  if (!linkId) return undefined;
  return chipEditor.chipModel.interfaceLinkMap.get(linkId);
}

export class SingleInterfaceTile extends InterfaceTileDefinition<SingleInterfaceTileStateBase> {
  graphics = singleInterfaceTileGraphicsDefinition;
  type = TileType.SingleInterface;

  name = 'Interface Tile (Single Signal)';
  canRotate = false;

  createActions: CreateActionsFn<SingleInterfaceTileState> = ({ instance, eventBus }) => {
    /** Sync toggleState to the paired tile via InterfaceLink and trigger its logic nodes. */
    const syncPairedTile = () => {
      const link = getInterfaceLinkForInstance(instance);
      if (!link) return;
      for (const tile of Object.values(link.tiles)) {
        if (!tile || tile === instance) continue;
        const pairedState = tile.state as SingleInterfaceTileState;
        pairedState.toggleState = instance.state.toggleState;
        tile.renderVersion++;
        if (!tile.logicNodeIds) continue;
        const nodeIds = distinct(Object.values(tile.logicNodeIds));
        for (const nodeId of nodeIds) {
          eventBus?.trigger(nodeId, 'Lever Flipped');
        }
      }
    };

    const triggerNodes = (reason: string) => {
      instance.renderVersion++;
      if (instance.logicNodeIds) {
        const nodeIds = distinct(Object.values(instance.logicNodeIds));
        for (const nodeId of nodeIds) {
          eventBus?.trigger(nodeId, reason);
        }
      }
      syncPairedTile();
    };

    return {
      toggle: () => {
        if (!sourceInputAllowed(instance.context)) return;
        if (instance.adapterPin || instance.state.inputMode !== SIInputMode.LEVER) return;
        instance.state.toggleState =
          instance.state.toggleState === Signal.HIGH ? Signal.LOW : Signal.HIGH;
        triggerNodes('Lever Flipped');
      },
      press: () => {
        if (!sourceInputAllowed(instance.context)) return;
        if (instance.adapterPin || instance.state.inputMode !== SIInputMode.BUTTON)
          return;
        instance.state.toggleState = Signal.HIGH;
        triggerNodes('Button Pressed');
      },
      depress: () => {
        if (!sourceInputAllowed(instance.context, true)) return;
        if (instance.adapterPin || instance.state.inputMode !== SIInputMode.BUTTON)
          return;
        instance.state.toggleState = Signal.LOW;
        triggerNodes('Button Released');
      },
    };
  };

  createContextMenuItems({
    instance,
    chipEditorModel,
  }: CreateContextMenuItemsData<
    InterfaceTileState<SingleInterfaceTileStateBase>
  >): Array<{ label: string; action: ContextMenuActionFn }> {
    const interfaceLink = chipEditorModel.chipModel.interfaceLinkMap.get(
      instance.state.connectedInterface.interfaceLinkId as InterfaceLinkInstanceId,
    );

    const currentMode = interfaceLink?.inputMode ?? SIInputMode.NONE;
    const setInputMode = (mode: SIInputMode) => {
      if (!interfaceLink) return;
      interfaceLink.inputMode = mode;
      instance.state.inputMode = mode;
      if (mode === SIInputMode.NONE) {
        instance.state.toggleState = Signal.LOW;
      }
      // Update paired tile if it exists
      for (const tile of Object.values(interfaceLink.tiles)) {
        if (tile && tile !== instance) {
          (tile.state as SingleInterfaceTileState).inputMode = mode;
          if (mode === SIInputMode.NONE) {
            (tile.state as SingleInterfaceTileState).toggleState = Signal.LOW;
          }
        }
      }
      chipEditorModel.chipModel.logicCircuit.rebuildGraph();
      invalidateInterfaceLinkTiles(interfaceLink);
    };

    const passive =
      instance.adapterPin || chipEditorModel.chipModel.kind === 'bundle-adapter';
    return [
      ...(!passive
        ? [
            {
              label: 'Set Input Mode',
              action: ((_takeInput, uiModel) => {
                uiModel.openSIInputModeDialog(currentMode, setInputMode);
              }) as ContextMenuActionFn,
            },
          ]
        : []),
      {
        label: interfaceLink?.labelHidden ? 'Show Label' : 'Hide Label',
        action: () => {
          if (!interfaceLink) return;
          interfaceLink.labelHidden = !interfaceLink.labelHidden;
          invalidateInterfaceLinkTiles(interfaceLink);
        },
      },
      ...super.createContextMenuItems({ instance, chipEditorModel }),
    ];
  }

  buildAndConnectNodes: BuildAndConnectNodesFn<SingleInterfaceTileState> = (data) => {
    const mode = data.instance.adapterPin
      ? SIInputMode.NONE
      : (data.instance.state.inputMode ?? SIInputMode.NONE);
    if (mode === SIInputMode.LEVER) {
      return standardBuildAndConnect('LEVER', data as any, {
        ...this,
        ioSides: allOutputSides,
      });
    }
    if (mode === SIInputMode.BUTTON) {
      return standardBuildAndConnect('BUTTON', data as any, {
        ...this,
        ioSides: allOutputSides,
      });
    }
    return wireBuildAndConnect(data as any);
  };

  needsStateToNodeSync = (instance: TileInstance<SingleInterfaceTileState>) =>
    !instance.adapterPin &&
    (instance.state.inputMode ?? SIInputMode.NONE) !== SIInputMode.NONE;

  syncStateToNodes = (data: {
    instance: TileInstance<SingleInterfaceTileState>;
    graph: LogicGraph;
  }) => {
    const { instance, graph } = data;
    const mode = instance.adapterPin
      ? SIInputMode.NONE
      : (instance.state.inputMode ?? SIInputMode.NONE);
    if (mode === SIInputMode.NONE) return;

    if (!instance.logicNodeIds) return;
    const nodeIds = distinct(Object.values(instance.logicNodeIds));
    for (const nodeId of nodeIds) {
      const node = graph.nodes.get(nodeId) as LogicNodeInstance<LeverNodeState>;
      if (!node) continue;
      node.state.isOn = instance.state.toggleState === Signal.HIGH;
    }
  };

  createDefaultState: CreateDefaultStateFn<SingleInterfaceTileState> = ({
    meta,
    globalTilePosition,
  }) => {
    return {
      ...super.createDefaultState({ meta, globalTilePosition }),
      inputMode: SIInputMode.NONE,
      toggleState: Signal.LOW,
    };
  };

  ioSides = allInputOutputSides;
}

export const singleInterfaceTileGraphicsDefinition: TileGraphicsDefinition<SingleInterfaceTileState> =
  {
    type: TileType.SingleInterface,
    draw: function (g, data): void {
      const { state, meta, connections, context, chipEditorModel } = data;
      const owner = meta.partOfChip
        ? chipEditorModel?.logicCircuitEditor.circuitModel.chips.find(
            (chip) => chip.id === meta.partOfChip,
          )?.chip
        : undefined;
      const preview = context?.editor?.placeChipModel.selectedChip;
      const passive =
        owner?.kind === 'bundle-adapter' ||
        (!meta.partOfChip && chipEditorModel?.chipModel.kind === 'bundle-adapter') ||
        (preview?.kind === 'bundle-adapter' &&
          preview.editingInstance.id === meta.partOfChip);
      const inputMode = passive
        ? SIInputMode.NONE
        : (state.inputMode ?? SIInputMode.NONE);

      defaultTileGraphics(g);

      const ioSides =
        inputMode !== SIInputMode.NONE ? allOutputSides : allInputOutputSides;
      connectionGraphics(
        g,
        colors.signalColor,
        colors.noSignalColor,
        meta,
        connections,
        ioSides,
        context,
      );

      const interfaceLink = getInterfaceLinkFromTile(
        chipEditorModel,
        meta,
        state,
        context ?? { isTileImageGeneration: false },
      );
      let interfaceColor: string = colors.signalColor;
      if (interfaceLink) {
        interfaceColor = interfaceLink.color;
      }

      const isHigh =
        inputMode !== SIInputMode.NONE
          ? state.toggleState === Signal.HIGH
          : Object.values(meta.outputs).some((sig) => sig === Signal.HIGH);

      const dimColor = new Color(interfaceColor)
        .multiply([colors.noSignalValue, colors.noSignalValue, colors.noSignalValue])
        .toHex();

      if (inputMode === SIInputMode.LEVER || inputMode === SIInputMode.BUTTON) {
        pixelControl(
          g,
          isHigh ? interfaceColor : dimColor,
          inputMode === SIInputMode.BUTTON,
          isHigh,
        );
      } else {
        pixelRect(g, 1, 1, 23, 23, isHigh ? interfaceColor : dimColor);
      }

      drawInterfaceTileGraphics<SingleInterfaceTileStateBase>(g, data);
    },
  };
